import type { FeatureSet } from "common/constants/features";

/**
 * Feature flags that affect casework behavior — a structural slice of `FeatureSet`, so any caller
 * holding a full feature set (`ed.enabledFeatures`, `useUserContext().enabledFeatures`) can pass it
 * straight through with no destructuring. Widen this `Pick` when a casework feature needs its own flag.
 *
 * `packages/doc` must never read the flag registry itself: casework constructors stay pure and
 * deterministic, and server-side callers have no access to request-scoped feature state. The flag is
 * always an input.
 *
 * ## Adding the next Casework V2 feature
 *
 * 1. **New or changed creation default** (depth, height, offset, line style): `caseworkCreationDefaults`
 *    (`doc/elements/wallCasework`) is unflagged — a field with a matching `CaseworkSettings` entry is
 *    *omitted* there so generation resolves it, and a field with nothing to inherit gets a constant from
 *    `common/constants/buildingObjects`. Both Studio entry points — the casework command and
 *    `RoomCaseworkInspector` — pick it up automatically. Assert it in
 *    `test/unit/house/geometry/tallCabinetAndFillerPanelCasework.test.ts`. Gate a creation default only
 *    if the new field is itself unreleased, in which case gate it at the call sites, not in here.
 * 2. **New inspector UI**: `const features = useEditorSelector((ed) => ed.enabledFeatures)`, then wrap
 *    the new UI in `isCaseworkV2Enabled(features) && ...`. This used to go through a
 *    `useCaseworkFeatures` hook (`lib/studio/hooks`) whose only job was narrowing the selector's
 *    return type to {@link CaseworkFeatures}, guarding against casework inspector code reaching for an
 *    unrelated flag off the full `FeatureSet`; it was removed as a needless wrapper, so that guard is
 *    now a matter of discipline rather than the type checker. Not for settings-inheritance UI: the
 *    placeholder / source tag rows in `WallCaseworkInspector` are unflagged, because the resolution
 *    they surface is (see 5) — gate those on `casework.type === "FIXED_V2"` instead, since only that
 *    type declares the field optional.
 * 3. **New Studio command**: use `caseworkV2EnabledAnd(...)` from `lib/studio/cmdDefPredicates` as the
 *    command's `isEnabled` predicate.
 * 4. **New doc field** (e.g. `CabinetShape` on cabinet descriptors): declare it **optional** in
 *    `building-types`, pick an absent-value meaning that matches today's behavior, and regenerate with
 *    `yarn higharc gen doc-schema` so both flag states validate. Write it only when the flag is on —
 *    see `cabinetShapeDefault` in `doc/defaults/caseworkDefaults` — and always honor an explicit value
 *    from the caller so copying V2 data with the flag off doesn't drop it. Do **not** add a backfill
 *    migration: migrations can't read flags, so they would write V2 data into every doc while the
 *    stream is dark. Backfill at graduation, if the field needs to become required at all.
 * 5. **Changing how existing casework generates** (geometry or measures): the settings-resolution
 *    mechanism this used to describe now ships unflagged. `resolveCaseworkFromSettings` in
 *    {@link ./caseworkSettingsResolution} is the pattern to copy when relaxing another field:
 *    declare it optional on `WallCaseworkFixed`, omit it at creation, and resolve it at generation
 *    time, so an absent field inherits from `CaseworkSettings` while an explicit one stays a user
 *    override. Generation reads no casework flag today.
 *    If a change genuinely needs flag gating, the plumbing to add is a `selectCasework` field
 *    alongside `componentsEnabled` — that projection is what cache-keys the stage, since it is what
 *    `makeGenerationStageComparator` compares — plus a `makeCasework` parameter. `caseworkV2` is
 *    already on `GenerateBuildingFeatureSet` and its converter
 *    (`building-types/building/buildingTypes`), though generation no longer reads it; three
 *    `Required<GenerateBuildingFeatureSet>` projections force a compile error if a new flag is
 *    dropped: the converter, procure's `toGenerationFeatures`, and `buildersController`'s showroom
 *    GLTF projection.
 *    Two traps that cost real time, both worth reading before relaxing another field:
 *    - `wallCaseworkFixedNew` must not give the field a **destructuring default**; those fire on
 *      `undefined`, so a spread of `{ field: undefined }` silently refills it. Emit it conditionally.
 *    - the doc schema generator only emits a named `id(...)` reference for a property whose type is
 *      exactly the alias. `Omit<Base, "field">` and redeclaring an inherited optional on a subtype both
 *      make it inline the referenced type — and `Omit` also drops `parentId`'s `DocEntityParentId`
 *      hierarchy metadata. Keep the field off the shared base instead.
 *    Note the tradeoff: a creation-time change only affects new casework, while a generation-time one
 *    re-renders existing casework too. An existing doc's explicit value survives as an override, but a
 *    field it never carried — `includeHardware` was the case here — starts resolving from settings and
 *    moves baselines.
 *    The override rule has to hold on *every* path that resolves the field, not just
 *    `resolveCaseworkShape`. `material` is the cautionary tale: `makeCasework`'s catalog branch assigned
 *    the settings material unconditionally, which was harmless only while `resolvedMaterial` was dead
 *    code. Returning it turned four silent overwrites on at once, repainting every existing catalog
 *    cabinet for any builder whose Casework Settings materials differ from `propDefault0` — invisible in
 *    CI, because every fixture uses the defaults. It is now guarded on `inheritsMaterial`, and
 *    `caseworkCatalogItemNew` omits `material` for the four cabinet types so new catalog items still
 *    inherit.
 * 6. **Graduating the flag**: flip `status` in `common/constants/features`, and at GA delete the legacy
 *    branch of each ternary before removing the key.
 */
export type CaseworkFeatures = Pick<FeatureSet, "caseworkV2">;

/**
 * The single place in the codebase that names the `caseworkV2` flag key. Omitted or sparse feature
 * sets read as disabled, matching the flag's `defaultValue`.
 */
export function isCaseworkV2Enabled(features: CaseworkFeatures | undefined): boolean {
  return !!features?.caseworkV2;
}
